import { getCollection } from '@adornis/baseql/server/collections.js';
import { AssignmentProposalMessage, Conversation } from '../../conversation.js';
import type { Exam, ExamState } from '../../exam.js';
import { handleSendAssignmentConfirmationMessage } from '../agents/uploadAgent.js';
import { canProcessBlankExam } from './file-assignment.js';
import { PROCESS_FILES_TOOL_NAME } from './process-files.tool.js';

export function sendAssignmentConfirmationMessageToolDef(examState: ExamState) {
  const blankExamAllowed = canProcessBlankExam(examState);

  return {
    type: 'function',
    function: {
      name: 'send_assignment_confirmation_message',
      description: `NUTZE DIESES TOOL um die Dateienzuweisung mit dem Nutzer zu besprechen. 
      Du sollst nur die Zuweisung von unverarbeiteten Dateien besprechen!

      WICHTIG:
      - Wenn mehr als eine Datei hochgeladen wurde und der Nutzer die Zuordnung nicht klar angegeben hat, MUSST du IMMER dieses Tool aufrufen.
      - Wenn du bereits send_assignment_confirmation_message aufgerufen hast und der Nutzer die Zuordnung bestätigt hat, rufe dieses Tool auf keinen Fall nochmal auf, sondern rufe direkt das ${PROCESS_FILES_TOOL_NAME} tool auf, damit die Verarbeitung fortgesetzt werden kann.
      - Wenn Dateien angehängt sind und die Zuordnung unklar ist, darfst du diese nicht frei erfinden oder direkt mit der Verarbeitung fortfahren.
      - Gib in diesem Fall zuerst den Zuweisungsvorschlag über dieses Tool aus.
      - ${blankExamAllowed ? 'Die Leere Klausur kann nur verwendet werden, solange die Aufgabenstruktur noch nicht erkannt wurde' : 'Die Leere Klausur kann nicht nachträglich verwendet werden, die Aufgabenstruktur wurde bereits erkannt'}

      Das Tool nimmt deine Vorschläge für die Dateizuweisung und erstellt automatisch:
      1. Eine lesbare Nachricht mit deinem Vorschlag
      2. Eine interaktive Drag-Drop Box, wo der Nutzer die Dateien anpassen kann. 

      Der Nutzer kann dann die Box verwenden um die Zuweisung zu korrigieren bevor es weitergeht.
      Schreibe den Vorschlag nicht nochmal gesondert in messageContent, er wird automatisch aus den anderen Feldern generiert.`,
      parameters: {
        type: 'object',
        properties: {
          messageContent: {
            type: 'string',
            description: 'Der sichtbare Nachrichtentext, den der Nutzer lesen soll (frei formuliert vom Agenten).',
          },
          Schülerabgaben: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Dateinamen der Schülerabgaben',
          },
          Musterlösung: {
            type: 'string',
            description: 'Dateiname der Musterlösung (optional)',
          },
          Bewertungsschema: {
            type: 'string',
            description: 'Dateiname des Bewertungsschemas (optional)',
          },
          ...(blankExamAllowed
            ? {
                LeereKlausur: {
                  type: 'string',
                  description: 'Dateiname der leeren Klausur (optional)',
                },
              }
            : {}),
        },
        required: ['messageContent'],
      },
    },
  };
}

export async function handleSendAssignmentMessage(exam: Exam, toolCallArgs: any): Promise<object> {
  // A blank exam is only usable until the task structure has been extracted from it. Afterwards the
  // category does not apply, so we record that as a fixed boolean on the message and never carry a
  // value for it — no magic placeholder in the data field.
  const allowBlankExam = canProcessBlankExam(exam?.examState);
  const leereKlausurArg =
    allowBlankExam && typeof toolCallArgs?.LeereKlausur === 'string' ? toolCallArgs.LeereKlausur : undefined;

  const proposalMessage = await handleSendAssignmentConfirmationMessage(
    {
      schuelerabgaben: Array.isArray(toolCallArgs?.Schülerabgaben) ? toolCallArgs.Schülerabgaben : [],
      musterloesung: typeof toolCallArgs?.Musterlösung === 'string' ? toolCallArgs.Musterlösung : undefined,
      bewertungsschema: typeof toolCallArgs?.Bewertungsschema === 'string' ? toolCallArgs.Bewertungsschema : undefined,
      leereKlausur: leereKlausurArg,
    },
    typeof toolCallArgs?.messageContent === 'string' ? toolCallArgs.messageContent : undefined,
  );

  await (
    await getCollection(Conversation)
  ).updateOne({ _id: exam.conversationId }, {
    $push: {
      messages: new AssignmentProposalMessage({
        content: proposalMessage.content,
        baseContent: proposalMessage.content,
        schuelerabgaben: proposalMessage.proposal.schuelerabgaben,
        musterloesung: proposalMessage.proposal.musterloesung,
        bewertungsschema: proposalMessage.proposal.bewertungsschema,
        leereKlausur: proposalMessage.proposal.leereKlausur,
        // Drives whether the UI shows the blank-exam slot; survives the panel's persist round-trip.
        allowBlankExam,
        active: true,
      }).toObject(),
    },
  } as any);

  const toolResultContent = 'Nachricht mit Zuweisungsvorschlag gesendet.';
  return { content: toolResultContent };
}

export function sendAssignmentConfirmationAvailability(_examState: ExamState): boolean {
  return true;
}
